/**
 * Service Planning API client. Thin wrappers over the shared `apiFetch` helper
 * (see ./api) that map to the Elysia /service-planning module.
 */
import type {
  NewArrangementInput,
  NewPlanAssignmentInput,
  NewPlanItemInput,
  NewServicePlanInput,
  NewSongInput,
  Paginated,
  PlanAssignmentDto,
  PlanItemDto,
  ReorderPlanItemsInput,
  ServicePlanDetail,
  ServicePlanDto,
  SongArrangementDto,
  SongDetail,
  SongDto,
  UpdateArrangementInput,
  UpdatePlanAssignmentInput,
  UpdatePlanItemInput,
  UpdateServicePlanInput,
  UpdateSongInput,
} from '@fc/core'
import { apiFetch } from './api'

export type {
  ServicePlanDto,
  ServicePlanDetail,
  PlanItemDto,
  PlanAssignmentDto,
  SongDto,
  SongDetail,
  SongArrangementDto,
  NewServicePlanInput,
  NewPlanItemInput,
  NewPlanAssignmentInput,
  NewSongInput,
  NewArrangementInput,
} from '@fc/core'

const enc = encodeURIComponent

// ---- Service Plans ----
export const listServicePlans = async (
  status?: 'draft' | 'published',
): Promise<ServicePlanDto[]> => {
  const data = await apiFetch<Paginated<ServicePlanDto>>(
    `/service-planning/service-plans${status ? `?status=${status}` : ''}`,
  )
  return data.items
}

export const getServicePlan = (id: string) =>
  apiFetch<ServicePlanDetail>(`/service-planning/service-plans/${enc(id)}`)

export const createServicePlan = (input: NewServicePlanInput) =>
  apiFetch<ServicePlanDetail>('/service-planning/service-plans', {
    method: 'POST',
    body: JSON.stringify(input),
  })

export const updateServicePlan = (id: string, input: UpdateServicePlanInput) =>
  apiFetch<ServicePlanDetail>(`/service-planning/service-plans/${enc(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })

export const deleteServicePlan = (id: string) =>
  apiFetch<void>(`/service-planning/service-plans/${enc(id)}`, { method: 'DELETE' })

// ---- Plan Items ----
export const addPlanItem = (planId: string, input: NewPlanItemInput) =>
  apiFetch<PlanItemDto>(`/service-planning/service-plans/${enc(planId)}/items`, {
    method: 'POST',
    body: JSON.stringify(input),
  })

export const reorderPlanItems = (planId: string, input: ReorderPlanItemsInput) =>
  apiFetch<PlanItemDto[]>(`/service-planning/service-plans/${enc(planId)}/items/reorder`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })

export const updatePlanItem = (planId: string, itemId: string, input: UpdatePlanItemInput) =>
  apiFetch<PlanItemDto>(`/service-planning/service-plans/${enc(planId)}/items/${enc(itemId)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })

export const removePlanItem = (planId: string, itemId: string) =>
  apiFetch<void>(`/service-planning/service-plans/${enc(planId)}/items/${enc(itemId)}`, {
    method: 'DELETE',
  })

// ---- Plan Assignments ----
export const addPlanAssignment = (planId: string, input: NewPlanAssignmentInput) =>
  apiFetch<PlanAssignmentDto>(`/service-planning/service-plans/${enc(planId)}/assignments`, {
    method: 'POST',
    body: JSON.stringify(input),
  })

export const updatePlanAssignment = (
  planId: string,
  assignmentId: string,
  input: UpdatePlanAssignmentInput,
) =>
  apiFetch<PlanAssignmentDto>(
    `/service-planning/service-plans/${enc(planId)}/assignments/${enc(assignmentId)}`,
    { method: 'PATCH', body: JSON.stringify(input) },
  )

export const removePlanAssignment = (planId: string, assignmentId: string) =>
  apiFetch<void>(
    `/service-planning/service-plans/${enc(planId)}/assignments/${enc(assignmentId)}`,
    { method: 'DELETE' },
  )

// ---- Songs ----
export const listSongs = async (q?: string): Promise<SongDto[]> => {
  const data = await apiFetch<Paginated<SongDto>>(
    `/service-planning/songs${q ? `?q=${enc(q)}` : ''}`,
  )
  return data.items
}

export const getSong = (id: string) => apiFetch<SongDetail>(`/service-planning/songs/${enc(id)}`)

export const createSong = (input: NewSongInput) =>
  apiFetch<SongDto>('/service-planning/songs', { method: 'POST', body: JSON.stringify(input) })

export const updateSong = (id: string, input: UpdateSongInput) =>
  apiFetch<SongDto>(`/service-planning/songs/${enc(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })

export const deleteSong = (id: string) =>
  apiFetch<void>(`/service-planning/songs/${enc(id)}`, { method: 'DELETE' })

// ---- Song Arrangements ----
export const addArrangement = (songId: string, input: NewArrangementInput) =>
  apiFetch<SongArrangementDto>(`/service-planning/songs/${enc(songId)}/arrangements`, {
    method: 'POST',
    body: JSON.stringify(input),
  })

export const updateArrangement = (
  songId: string,
  arrangementId: string,
  input: UpdateArrangementInput,
) =>
  apiFetch<SongArrangementDto>(
    `/service-planning/songs/${enc(songId)}/arrangements/${enc(arrangementId)}`,
    { method: 'PATCH', body: JSON.stringify(input) },
  )

export const removeArrangement = (songId: string, arrangementId: string) =>
  apiFetch<void>(`/service-planning/songs/${enc(songId)}/arrangements/${enc(arrangementId)}`, {
    method: 'DELETE',
  })
